/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('DOMContentLoaded', function () {
    // Function to update columns
    function updateGrid(val) {
        let columns = val;

        // Ensure this only applies deeply on mobile devices.
        // If wider (desktop), remove the styles so the CSS logic prevails.
        const isMobile = window.innerWidth < 741;

        // Toggle visibility class only on mobile (CSS media query handles its effect per-device)
        if (columns === 4 && isMobile) {
            document.body.classList.add('view-4-columns');
        } else {
            document.body.classList.remove('view-4-columns');
        }

        const section = document.querySelector('.shopify-section--main-collection');
        const searchSection = document.querySelector('.shopify-section--main-search');
        const featuredSection = document.querySelector('.shopify-section--featured-collections');

        if (isMobile) {
            if (section) section.style.setProperty('--section-products-per-row', columns);
            if (searchSection) searchSection.style.setProperty('--section-products-per-row', columns);
            if (featuredSection) featuredSection.style.setProperty('--section-products-per-row', columns);

            // Also try to update the specific container
            if (section) {
                const productList = section.querySelector('.product-list');
                if (productList) productList.style.setProperty('--section-products-per-row', columns);
            }
            if (featuredSection) {
                const productList = featuredSection.querySelector('.product-list');
                if (productList) productList.style.setProperty('--section-products-per-row', columns);
            }
        } else {
            // Remove inline style to let CSS media queries take over for Desktop
            if (section) section.style.removeProperty('--section-products-per-row');
            if (searchSection) searchSection.style.removeProperty('--section-products-per-row');
            if (featuredSection) featuredSection.style.removeProperty('--section-products-per-row');
            if (section) {
                const productList = section.querySelector('.product-list');
                if (productList) productList.style.removeProperty('--section-products-per-row');
            }
            if (featuredSection) {
                const productList = featuredSection.querySelector('.product-list');
                if (productList) productList.style.removeProperty('--section-products-per-row');
            }
        }
    }

    // Set up click handlers for the new view toggle buttons
    const viewButtons = document.querySelectorAll('.mobile-toolbar__view-btn');
    if (viewButtons.length > 0) {
        // Initialize with the active one
        const activeBtn = document.querySelector('.mobile-toolbar__view-btn.is-active');
        if (activeBtn) {
            updateGrid(parseInt(activeBtn.getAttribute('data-view')));
        } else {
            updateGrid(2); // default 2 col
        }

        viewButtons.forEach(function(btn) {
            btn.addEventListener('click', function() {
                // Update active state
                viewButtons.forEach(b => b.classList.remove('is-active'));
                this.classList.add('is-active');

                // Update grid
                const viewVal = parseInt(this.getAttribute('data-view'));
                updateGrid(viewVal);
            });
        });

        // Update on resize to ensure desktop layout restores if window is resized
        let resizeTimer;
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function () {
                const currentActiveBtn = document.querySelector('.mobile-toolbar__view-btn.is-active');
                if (currentActiveBtn) {
                    updateGrid(parseInt(currentActiveBtn.getAttribute('data-view')));
                }
            }, 150);
        });
    }

    // Event delegation for Mobile Sort Pills and Clear Sort button
    document.addEventListener('click', function(e) {
        // 1. Mobile Sort Pills
        const pill = e.target.closest('.mobile-sort-pill');
        if (pill) {
            var sortValue = pill.getAttribute('data-sort-value');
            var form = document.getElementById('facet-filters-form');
            if (form) {
                var sortInput = form.querySelector('input[name="sort_by"]');
                if (sortInput) {
                    sortInput.value = sortValue;
                }
                
                // Update active styling immediately for visual feedback
                document.querySelectorAll('.mobile-sort-pill').forEach(function(p) {
                    p.classList.remove('is-active');
                });
                pill.classList.add('is-active');
                
                // Trigger AJAX update
                var formData = new FormData(form);
                var searchParams = new URLSearchParams(formData);
                var newUrl = window.location.pathname + '?' + searchParams.toString();
                
                var facetFilters = document.querySelector('facet-filters');
                if (facetFilters) {
                    facetFilters.dispatchEvent(new CustomEvent('facet:criteria-changed', {
                        bubbles: true,
                        detail: { url: newUrl }
                    }));
                }
            }
        }

        // 2. Clear Sort button in active tags
        const clearSortBtn = e.target.closest('[data-action="clear-sort"]');
        if (clearSortBtn) {
            e.preventDefault();
            var form = document.getElementById('facet-filters-form');
            if (form) {
                var sortInput = form.querySelector('input[name="sort_by"]');
                if (sortInput) {
                    sortInput.value = clearSortBtn.getAttribute('data-default-sort');
                }
                
                // Trigger AJAX update
                var formData = new FormData(form);
                var searchParams = new URLSearchParams(formData);
                var newUrl = window.location.pathname + '?' + searchParams.toString();
                
                var facetFilters = document.querySelector('facet-filters');
                if (facetFilters) {
                    facetFilters.dispatchEvent(new CustomEvent('facet:criteria-changed', {
                        bubbles: true,
                        detail: { url: newUrl }
                    }));
                }
            }
        }
    });
});